import { doc, getDoc, type Firestore } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';

import { getStorageClient } from '@/lib/firebase/client';
import { COLLECTIONS } from '@/lib/firebase/paths';
import { sanitizeHomepageSettings } from './homepage-settings-schema';
import { slugify } from '@/lib/utils';
import type { HomepageSettingsDoc } from '@/types/platform';

const HOMEPAGE_DOC_ID = 'homepage';

export async function getHomepageSettings(db: Firestore): Promise<HomepageSettingsDoc | null> {
  const snapshot = await getDoc(doc(db, COLLECTIONS.platformSettings, HOMEPAGE_DOC_ID));
  if (!snapshot.exists()) return null;
  return sanitizeHomepageSettings(snapshot.data());
}

export async function uploadHomepageImage(input: { ownerUid: string; file: File; slot: string }) {
  const storage = getStorageClient();
  if (!storage) throw new Error('Firebase Storage is not configured.');
  const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
  if (!allowedTypes.has(input.file.type)) {
    throw new Error('Upload a JPG, PNG, or WebP image.');
  }
  if (input.file.size > 5 * 1024 * 1024) {
    throw new Error('Image must be 5 MB or smaller.');
  }
  const extension = input.file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const safeSlot = slugify(input.slot || 'site-image') || 'site-image';
  const storagePath = `platform/homepage/${input.ownerUid}/${safeSlot}-${Date.now()}.${extension}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, input.file, {
    contentType: input.file.type || 'image/jpeg',
    cacheControl: 'public,max-age=31536000,immutable',
  });
  return {
    imageUrl: await getDownloadURL(storageRef),
    storagePath,
  };
}
